import { useCallback, useEffect, useMemo, useState } from 'react'

// Read the Telegram WebApp instance from the global scope (injected by telegram-web-app.js)
const getWebApp = () => window?.Telegram?.WebApp || null

export const useTgData = () => {
  const [webApp, setWebApp] = useState(getWebApp)

  // Wait for the Telegram WebApp SDK to be injected, then signal the app is ready
  useEffect(() => {
    if (webApp) {
      // Tell Telegram the mini-app is fully loaded and can be displayed
      webApp.ready()

      return undefined
    }

    //
    //

    const intervalId = setInterval(() => {
      const tg = getWebApp()

      if (tg) {
        clearInterval(intervalId)

        setWebApp(tg)
      }
    }, 250)

    return () => clearInterval(intervalId)
  }, [webApp])

  //
  //

  // Send structured data to the bot (works only when the app is opened via a keyboard button)
  const sendData = useCallback(
    (data) => {
      if (!webApp) {
        return
      }

      const payload = typeof data === 'string' ? data : JSON.stringify(data)

      webApp.sendData(payload)
    },
    [webApp]
  )

  // Send a plain text message to the bot
  const sendMessage = useCallback(
    (text) => {
      if (!webApp) {
        return
      }

      webApp.sendData(String(text))
    },
    [webApp]
  )

  //
  //

  // Show a native alert to the user
  const showAlert = useCallback(
    (message, callback) => {
      if (!webApp) {
        return
      }

      webApp.showAlert(String(message), callback)
    },
    [webApp]
  )

  // Show a native popup with custom title/buttons to the user
  const showPopup = useCallback(
    (params, callback) => {
      if (!webApp) {
        return
      }

      webApp.showPopup(params, callback)
    },
    [webApp]
  )

  //
  //

  // Close the mini-app
  const close = useCallback(() => {
    webApp?.close()
  }, [webApp])

  //
  //

  return useMemo(
    () => ({
      webApp,
      //
      isReady: Boolean(webApp),
      //
      initData: webApp?.initData ?? null,
      initDataUnsafe: webApp?.initDataUnsafe ?? null,
      user: webApp?.initDataUnsafe?.user ?? null,
      languageCode: webApp?.initDataUnsafe?.user?.language_code ?? null,
      //
      sendData,
      sendMessage,
      showAlert,
      showPopup,
      close,
    }),
    [webApp, sendData, sendMessage, showAlert, showPopup, close]
  )
}
